import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface AdminInfo {
  id: number
  username: string
  name: string
  role: string
  permissions?: string[]
  lastLoginTime?: number
}

// 权限标识 -> 路由名称
export const PERMISSION_ROUTE: Record<string, string> = {
  dashboard: 'admin-dashboard',
  users: 'admin-users',
  'sign-statistics': 'admin-sign-statistics',
  logs: 'admin-logs',
  announcements: 'admin-announcements',
  config: 'admin-config',
  'watch-keys': 'admin-watch-keys',
  agents: 'admin-agents',
  abuse: 'admin-abuse'
}

// 登录后默认落地的页面优先级
const HOME_ORDER = ['dashboard', 'users', 'sign-statistics', 'logs', 'announcements', 'config', 'watch-keys']

export const useAdminStore = defineStore('admin', () => {
  const adminInfo = ref<AdminInfo | null>(null)
  const token = ref<string | null>(null)

  const isLoggedIn = computed(() => {
    return !!token.value && !!adminInfo.value
  })

  const isSuperAdmin = computed(() => adminInfo.value?.role === 'admin')

  const hasPermission = (permission: string) => {
    if (!adminInfo.value) return false
    if (adminInfo.value.role === 'admin') return true
    const permissions = adminInfo.value.permissions || []
    return permissions.includes(permission)
  }

  const homeRouteName = computed(() => {
    if (!adminInfo.value) return 'admin-login'
    if (adminInfo.value.role === 'admin') return 'admin-dashboard'
    for (const key of HOME_ORDER) {
      if ((adminInfo.value.permissions || []).includes(key)) {
        return PERMISSION_ROUTE[key] || 'admin-sign-statistics'
      }
    }
    return 'admin-sign-statistics'
  })

  const login = (adminToken: string, info: AdminInfo) => {
    token.value = adminToken
    adminInfo.value = info
  }

  const logout = () => {
    token.value = null
    adminInfo.value = null
  }

  const updateAdminInfo = (info: Partial<AdminInfo>) => {
    if (adminInfo.value) {
      adminInfo.value = { ...adminInfo.value, ...info }
    }
  }

  return {
    adminInfo,
    token,
    isLoggedIn,
    isSuperAdmin,
    hasPermission,
    homeRouteName,
    login,
    logout,
    updateAdminInfo
  }
}, {
  persist: {
    key: 'admin-store',
    storage: localStorage
  }
})
